import React from 'react';
import { IconPicker, getIconComponent } from './IconPicker';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { Lock, MessageSquare, Folder, BookText, File, ChevronDown, Check, Plus } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { cn } from '../lib/utils';
import { InlineEditableText } from './InlineEditableText';
import { sortProjectExplorerDocuments } from '../utils/sort-project-explorer-documents';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export interface SwitchableDocument {
  id: string;
  title: string;
  icon: string | null;
  pinned?: boolean;
}

interface EditableDocumentHeaderProps {
  documentTitle: string;
  projectTitle: string;
  documentIcon: string | null;
  onDocumentTitleChange: (title: string) => void | boolean | Promise<void | boolean>;
  onProjectTitleChange: (title: string) => void | boolean | Promise<void | boolean>;
  onIconChange: (icon: string | null) => void;
  isReadOnly?: boolean;
  ownerEmail?: string | null;
  hasCommentPermission?: boolean;
  isMainDocument?: boolean;
  sectionLabel?: string | null;
  sectionIcon?: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  documents?: SwitchableDocument[];
  currentDocumentId?: string | null;
  mainDocumentId?: string | null;
  onSelectDocument?: (documentId: string) => void;
  onCreateDocument?: () => void;
  canCreateDocument?: boolean;
}

export const EditableDocumentHeader: React.FC<EditableDocumentHeaderProps> = ({
  documentTitle,
  projectTitle,
  documentIcon,
  onDocumentTitleChange,
  onProjectTitleChange,
  onIconChange,
  isReadOnly = false,
  ownerEmail,
  hasCommentPermission = false,
  isMainDocument = false,
  sectionLabel = null,
  sectionIcon: SectionIcon,
  documents = [],
  currentDocumentId = null,
  mainDocumentId = null,
  onSelectDocument,
  onCreateDocument,
  canCreateDocument = false,
}) => {
  const isMobile = useIsMobile();
  const { t } = useLocale();
  const showDocumentMenu = Boolean(onSelectDocument && currentDocumentId);
  const titleClassName = cn('truncate leading-normal font-semibold', isMobile ? 'text-sm' : 'text-base');

  const sortedDocuments = (() => {
    const list = [...documents];
    if (
      currentDocumentId &&
      !list.some((doc) => doc.id === currentDocumentId)
    ) {
      list.push({
        id: currentDocumentId,
        title: documentTitle,
        icon: documentIcon,
      });
    }
    return sortProjectExplorerDocuments(list, mainDocumentId);
  })();

  return (
    <TooltipProvider>
      <div className={cn('flex min-w-0', isMobile ? 'flex-col items-start gap-0' : 'items-center gap-2')} style={{ flex: 1 }}>
        {projectTitle && (
          <div className={cn('flex min-w-0 items-center gap-2', isMobile ? 'max-w-full' : 'shrink-0')}>
            <Folder className="!h-4 !w-4 shrink-0" />
            <InlineEditableText
              value={projectTitle}
              onCommit={onProjectTitleChange}
              savedFieldLabel={t('project_title')}
              readOnly={isReadOnly}
              placeholder={t('untitled') || 'Untitled'}
              tooltip={isReadOnly ? projectTitle : undefined}
              className={cn(titleClassName, !isMobile && 'max-w-[250px]')}
            />
          </div>
        )}
        <div className={cn('flex min-w-0 items-center gap-2', isMobile && 'w-full')}>
          {!isMobile && projectTitle && <span className="shrink-0 text-muted-foreground">/</span>}
          {isReadOnly && ownerEmail && (
            <Tooltip>
              <TooltipTrigger asChild>
                {hasCommentPermission ? (
                  <MessageSquare className="h-4 w-4" style={{ color: '#6b7280', flexShrink: 0 }} />
                ) : (
                  <Lock className="h-4 w-4" style={{ color: '#6b7280', flexShrink: 0 }} />
                )}
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start">
                <p>
                  {hasCommentPermission
                    ? t('document_shared_as_commenter') || 'You can read and comment on this document'
                    : `${t('read_only_mode')}: ${t('document_shared_as_reader') || 'You have read-only access to this document'}`}
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          {!isMobile && (
            <IconPicker
              selectedIcon={documentIcon}
              onSelectIcon={onIconChange}
              disabled={isReadOnly}
              size={16}
              buttonSize={28}
              fallbackIcon={isMainDocument ? BookText : File}
            />
          )}
          <div className="flex min-w-0 flex-1 items-center gap-0.5">
            <InlineEditableText
              value={documentTitle}
              onCommit={onDocumentTitleChange}
              savedFieldLabel={t('document_title')}
              readOnly={isReadOnly}
              placeholder={t('untitled_document') || 'Untitled'}
              tooltip={isReadOnly ? (documentTitle || t('untitled_document') || 'Untitled') : undefined}
              className={cn(titleClassName, 'flex-1')}
              inputClassName="flex-1"
            />
            {showDocumentMenu && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex shrink-0 items-center justify-center rounded-md p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label={t('switch_document') || 'Switch document'}
                    title={t('switch_document') || 'Switch document'}
                  >
                    <ChevronDown className="!h-4 !w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[220px] max-w-[min(24rem,calc(100vw-2rem))]">
                  {sortedDocuments.map((doc) => {
                    const DocIcon = doc.icon ? getIconComponent(doc.icon) : null;
                    const Icon = DocIcon ?? (doc.id === mainDocumentId ? BookText : File);
                    const isSelected = doc.id === currentDocumentId;
                    return (
                      <DropdownMenuItem
                        key={doc.id}
                        onClick={() => {
                          if (!isSelected && onSelectDocument) {
                            onSelectDocument(doc.id);
                          }
                        }}
                        className="gap-2"
                      >
                        <Icon className="!h-4 !w-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{doc.title || t('untitled_document')}</span>
                        {isSelected && <Check className="!h-4 !w-4 shrink-0" />}
                      </DropdownMenuItem>
                    );
                  })}
                  {canCreateDocument && onCreateDocument && !isReadOnly && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={onCreateDocument} className="gap-2">
                        <Plus className="!h-4 !w-4 shrink-0" />
                        <span>{t('new document')}</span>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {sectionLabel && (
            <>
              <span className="shrink-0 text-muted-foreground">/</span>
              <div className="flex min-w-0 items-center gap-2">
                {SectionIcon && <SectionIcon className="shrink-0" style={{ width: 16, height: 16 }} />}
                <span className={cn('min-w-0 truncate leading-normal font-semibold', isMobile ? 'text-sm' : 'text-base')}>
                  {sectionLabel}
                </span>
              </div>
            </>
          )}
          {isReadOnly && ownerEmail && (
            <span className="shrink-0 truncate text-muted-foreground" style={{ fontSize: '12px' }}>
              {ownerEmail}
            </span>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
